import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { authApi, profileApi, walletApi } from "../services/api";

import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from "@mui/material";

import { countryCodes, defaultCountryCode } from "../constants/countryCodes";
import { indianStates } from "../constants/indianStates";
import { hasValidationErrors, validateProfileForm } from "../utils/formValidation";

const getProfileForm = (profile) => ({
  firstName: profile?.firstName || "",
  lastName: profile?.lastName || "",
  mobileCountryCode: profile?.mobileCountryCode || defaultCountryCode,
  mobile: profile?.mobile || profile?.phone || "",
  address: profile?.address || "",
  city: profile?.city || "",
  state: profile?.state || "",
  postalCode: profile?.postalCode || ""
});

const Profile = () => {
  const { user, updateUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(getProfileForm(null));
  const [isEditing, setIsEditing] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [walletInfo, setWalletInfo] = useState({
    referralCodeUsed: false,
    referralCode: null
  });

  const [otp, setOtp] = useState("");
  const [openOtpModal, setOpenOtpModal] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await profileApi.get();
        const fetchedUser = res?.data?.user || res?.data;

        setProfile(fetchedUser);
        setForm(getProfileForm(fetchedUser));
      } catch {
        console.error("Failed to fetch profile");
      } finally {
        setLoading(false);
      }
    };

    const fetchWalletInfo = async () => {
      try {
        const balanceRes = await walletApi.getBalance();
        const referralRes = await walletApi.getReferralCode();
        
        setWalletInfo({
          referralCodeUsed: balanceRes?.data?.referralCodeUsed ?? false,
          referralCode: referralRes?.data?.referralCode || null
        });
      } catch (err) {
        console.error("Failed to load wallet info", err);
      }
    };

    fetchProfile();
    fetchWalletInfo();
  }, []);


  const handleChange = (e) => {
    const { name, value } = e.target;

    const nextValue =
      name === "mobile"
        ? value.replace(/\D/g, "").slice(0, 10)
        : name === "postalCode"
        ? value.replace(/\D/g, "").slice(0, 6)
        : value;

    setForm({ ...form, [name]: nextValue });
  };

 const handleSave = async () => {

  const validationErrors =
    validateProfileForm(form);

  if (
    hasValidationErrors(validationErrors)
  ) {
    return;
  }

  setSaving(true);

  try {

    // =========================
    // UPDATE PROFILE
    // =========================
    await profileApi.update(form);

    // =========================
    // FETCH LATEST PROFILE
    // =========================
    const refreshedProfile =
      await profileApi.get();

    const updatedUser =
      refreshedProfile?.data?.user ||
      refreshedProfile?.data;

    // =========================
    // UPDATE STATES
    // =========================
    setProfile(updatedUser);

    setForm(
      getProfileForm(updatedUser)
    );

    updateUser(updatedUser);

    // =========================
    // EXIT EDIT MODE
    // =========================
    setIsEditing(false);

  } catch (err) {

    console.error(
      "Update failed:",
      err
    );

  } finally {

    setSaving(false);

  }

};

  // =========================
  // EMAIL OTP
  // =========================
  const handleSendEmailOtp = async () => {
    await authApi.sendEmailOtp();
    setOpenOtpModal(true);
  };

  const handleVerifyOtp = async () => {
    try {
      await authApi.verifyEmailOtp(otp);

      setProfile((prev) => ({
        ...prev,
        emailVerified: true
      }));

      setOpenOtpModal(false);
    } catch {
      alert("Invalid OTP");
    }
  };

  if (!user) return <Typography textAlign="center">Not logged in</Typography>;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
  <Box
    sx={{
      minHeight: "100vh",
      background:
        "linear-gradient(135deg,#fff7f5 0%, #f8ece8 100%)",
      py: 6,
      px: 2
    }}
  >

    <Box
      display="flex"
      justifyContent="center"
    >

      <Card
        sx={{
          width: "100%",
          maxWidth: 900,
          borderRadius: "30px",
          overflow: "hidden",
          boxShadow:
            "0 20px 50px rgba(139,0,0,0.12)",
          border:
            "1px solid rgba(139,0,0,0.08)"
        }}
      >

        {/* HEADER */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg,#5c0000 0%, #8B0000 100%)",
            color: "#fff",
            px: 5,
            py: 5,
            position: "relative"
          }}
        >

          <Box
            display="flex"
            alignItems="center"
            gap={3}
          >

            <Avatar
              sx={{
                width: 90,
                height: 90,
                fontSize: 34,
                fontWeight: 700,
                bgcolor: "#fff",
                color: "#8B0000",
                border:
                  "4px solid rgba(255,255,255,0.25)"
              }}
            >
              {profile?.firstName?.[0]}
            </Avatar>

            <Box>

              <Typography
                variant="h4"
                fontWeight={700}
              >
                {profile?.firstName}{" "}
                {profile?.lastName}
              </Typography>

              <Typography
                sx={{
                  opacity: 0.85,
                  mt: 1,
                  fontSize: 15
                }}
              >
                Manage your personal
                information and account
                details
              </Typography>

            </Box>

          </Box>

        </Box>

        {/* CONTENT */}
        <CardContent
          sx={{
            p: {
              xs: 3,
              md: 5
            }
          }}
        >

          {/* EMAIL SECTION */}
          <Box
            sx={{
              background:
                "linear-gradient(135deg,#fff8f6,#fff)",
              border:
                "1px solid rgba(139,0,0,0.12)",
              borderRadius: "20px",
              p: 3,
              mb: 4
            }}
          >

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={{
                xs: "flex-start",
                md: "center"
              }}
              flexDirection={{
                xs: "column",
                md: "row"
              }}
              gap={2}
            >

              <Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#7b7b7b",
                    mb: 0.5
                  }}
                >
                  Email Address
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 18,
                    color: "#2b2b2b"
                  }}
                >
                  {profile?.email}
                </Typography>

              </Box>

              <Box
                display="flex"
                alignItems="center"
                gap={1.5}
              >

                <Box
                  sx={{
                    px: 2,
                    py: 0.8,
                    borderRadius: "30px",
                    fontSize: 13,
                    fontWeight: 700,
                    color: profile?.emailVerified
                      ? "#166534"
                      : "#8B0000",
                    backgroundColor:
                      profile?.emailVerified
                        ? "#dcfce7"
                        : "#fde8e8"
                  }}
                >
                  {profile?.emailVerified
                    ? "Verified"
                    : "Not Verified"}
                </Box>

                {!profile?.emailVerified && (
                  <Button
                    variant="contained"
                    onClick={handleSendEmailOtp}
                    sx={{
                      borderRadius: "12px",
                      px: 3,
                      py: 1,
                      fontWeight: 700,
                      textTransform: "none",
                      background:
                        "linear-gradient(135deg,#5c0000,#8B0000)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg,#470000,#700000)"
                      }
                    }}
                  >
                    Verify Email
                  </Button>
                )}

              </Box>

            </Box>

          </Box>

          {/* REFERRAL STATUS */}
          <Box
            sx={{
              background: "linear-gradient(135deg,#fff9f7,#fff)",
              border: "1px solid rgba(139,0,0,0.12)",
              borderRadius: "20px",
              p: 3,
              mb: 4
            }}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              mb={2}
              sx={{ color: "#5c0000" }}
            >
              Referral Status
            </Typography>

            <Box
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Box
                sx={{
                  px: 2,
                  py: 0.8,
                  borderRadius: "30px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: walletInfo.referralCodeUsed
                    ? "#166534"
                    : "#8B0000",
                  backgroundColor: walletInfo.referralCodeUsed
                    ? "#dcfce7"
                    : "#fde8e8"
                }}
              >
                {walletInfo.referralCodeUsed
                  ? "Referral Code Used"
                  : "No Referral Code Used"}
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "#7b7b7b" }}
              >
                {walletInfo.referralCodeUsed
                  ? "You have applied a referral code"
                  : "You can apply a referral code on Wallet page"}
              </Typography>
            </Box>
          </Box>

          {/* SECTION TITLE */}
          <Typography
            variant="h5"
            fontWeight={700}
            mb={4}
            sx={{
              color: "#5c0000"
            }}
          >
            Personal Information
          </Typography>

          {/* FORM */}
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              md: "1fr 1fr"
            }}
            gap={3}
          >

            <TextField
              label="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              disabled={!isEditing}
              fullWidth
            />

            <TextField
              label="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              disabled={!isEditing}
              fullWidth
            />

            <Box display="flex" gap={1}>

              <TextField
                select
                name="mobileCountryCode"
                value={form.mobileCountryCode}
                onChange={handleChange}
                disabled={!isEditing}
                sx={{ width: 120 }}
              >
                {countryCodes.map((c) => (
                  <MenuItem
                    key={c.code}
                    value={c.code}
                  >
                    {c.code}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Mobile Number"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                disabled={!isEditing}
                fullWidth
              />

            </Box>

            <TextField
              label="Postal Code"
              name="postalCode"
              value={form.postalCode}
              onChange={handleChange}
              disabled={!isEditing}
              fullWidth
            />

            <TextField
              label="Address"
              name="address"
              value={form.address}
              onChange={handleChange}
              disabled={!isEditing}
              fullWidth
              multiline
              rows={3}
              sx={{
                gridColumn: {
                  md: "1 / span 2"
                }
              }}
            />

            <TextField
              label="City"
              name="city"
              value={form.city}
              onChange={handleChange}
              disabled={!isEditing}
              fullWidth
            />

            <TextField
              select
              label="State"
              name="state"
              value={form.state}
              onChange={handleChange}
              disabled={!isEditing}
              fullWidth
            >
              {indianStates.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

          </Box>

          {/* BUTTONS */}
          <Box mt={5}>

            {!isEditing ? (

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() =>
                  setIsEditing(true)
                }
                sx={{
                  py: 1.8,
                  borderRadius: "16px",
                  fontWeight: 700,
                  fontSize: 16,
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg,#5c0000,#8B0000)",
                  boxShadow:
                    "0 10px 25px rgba(139,0,0,0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#470000,#700000)"
                  }
                }}
              >
                Edit Profile
              </Button>

            ) : (

              <Box display="flex" gap={2}>

                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() =>
                    setIsEditing(false)
                  }
                  sx={{
                    py: 1.8,
                    borderRadius: "16px",
                    fontWeight: 700,
                    textTransform: "none",
                    borderColor: "#8B0000",
                    color: "#8B0000"
                  }}
                >
                  Cancel
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSave}
                  sx={{
                    py: 1.8,
                    borderRadius: "16px",
                    fontWeight: 700,
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg,#5c0000,#8B0000)",
                    boxShadow:
                      "0 10px 25px rgba(139,0,0,0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg,#470000,#700000)"
                    }
                  }}
                >
                  {saving ? (
                    <CircularProgress
                      size={22}
                      sx={{ color: "#fff" }}
                    />
                  ) : (
                    "Save Changes"
                  )}
                </Button>

              </Box>

            )}

          </Box>

          {/* OTP MODAL */}
          <Dialog
            open={openOtpModal}
            onClose={() =>
              setOpenOtpModal(false)
            }
            PaperProps={{
              sx: {
                borderRadius: "24px",
                width: 420,
                p: 1
              }
            }}
          >

            <DialogTitle
              sx={{
                fontWeight: 700,
                color: "#5c0000"
              }}
            >
              Verify Email
            </DialogTitle>

            <DialogContent>

              <Typography
                mb={2}
                color="text.secondary"
              >
                Enter the OTP sent to your
                email address
              </Typography>

              <TextField
                fullWidth
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value)
                }
              />

            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                pb: 3
              }}
            >

              <Button
                onClick={() =>
                  setOpenOtpModal(false)
                }
                sx={{
                  color: "#8B0000",
                  fontWeight: 700
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleVerifyOtp}
                sx={{
                  borderRadius: "12px",
                  px: 3,
                  fontWeight: 700,
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg,#5c0000,#8B0000)"
                }}
              >
                Verify OTP
              </Button>

            </DialogActions>

          </Dialog>

        </CardContent>

      </Card>

    </Box>

  </Box>
);
};

export default Profile;